import dynamic from 'next/dynamic'
import { type FC } from 'react'

import {
  LocationButton,
  LocationIconContainer,
  LocationName,
  LocationRadius,
} from '@/components/shared/global-header/search-bar/styled'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useListingsDrawerState } from '@/hooks/srp/useListingsDrawerState'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_CTA_HEADER } from '@/lib/ga/constants/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'

const LocationOutlineIcon = dynamic(() => import('@kijiji/icons/src/icons/LocationOutline'), {
  ssr: false,
})

type SearchLocationProps = {
  id: string
  hideOnMobile?: boolean
}

export const SearchLocation: FC<SearchLocationProps> = ({ id, hideOnMobile }) => {
  const { location } = useGetLocation()
  const { apiLocale } = useLocale()
  const { isListingsDrawerOpen, setListingsDrawerState } = useListingsDrawerState()

  const { handleOpenModal } = useModal(MODAL_REGISTRY.SEARCH_LOCATION)

  const handleLocationClick = () => {
    trackEvent({ action: GA_EVENT.LocationSelectBegin, label: GA_CTA_HEADER })
    // Close the listings drawer if it is open to prevent overlapping
    // with the location modal
    if (isListingsDrawerOpen) {
      setListingsDrawerState({ isOpen: false })
    }

    handleOpenModal()
  }

  /**
   * It should use location name if is a region, or address with fallback on the name if is not an exact location.
   */
  const locationName = location.isRegion
    ? location.name[apiLocale]
    : location.area?.address || location.name[apiLocale]

  return (
    <>
      <LocationButton
        hideOnMobile={hideOnMobile}
        data-testid={id}
        forwardedAs="button"
        onClick={handleLocationClick}
        size="medium"
        variant="secondary"
      >
        <LocationIconContainer>
          <LocationOutlineIcon aria-hidden="true" />
        </LocationIconContainer>

        <LocationName data-testid="location-name">{locationName}</LocationName>

        {!location.isRegion && !!location.area && (
          <LocationRadius data-testid="location-radius">
            {` • `}
            {location.area.radius}km
          </LocationRadius>
        )}
      </LocationButton>
    </>
  )
}
